import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getAllUsers, updateUser, resetUserPassword, deleteUser } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    ArrowLeft, 
    Users, 
    Search,
    Pencil, 
    Key,
    Trash2,
    Loader2,
    Save,
    Mail,
    Building2,
    Phone,
    Copy,
    Check
} from 'lucide-react';
import { toast } from 'sonner';

export default function UsersManagementPage() {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Edit user dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({});
    
    // Reset password dialog
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetUser, setResetUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [sendEmail, setSendEmail] = useState(true);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [copied, setCopied] = useState(false);
    
    // Delete dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!isAdmin()) {
            navigate('/operator');
            return;
        }
        loadUsers();
    }, []);

    useEffect(() => {
        filterUsers();
    }, [searchQuery, users]);

    const loadUsers = async () => {
        try {
            const response = await getAllUsers();
            setUsers(response.data);
            setFilteredUsers(response.data);
        } catch (error) {
            toast.error('Errore nel caricamento degli utenti');
        } finally {
            setLoading(false);
        }
    };

    const filterUsers = () => {
        if (!searchQuery.trim()) {
            setFilteredUsers(users);
            return;
        }
        
        const query = searchQuery.toLowerCase();
        const filtered = users.filter(user => 
            user.email?.toLowerCase().includes(query) ||
            user.nome?.toLowerCase().includes(query) ||
            user.cognome?.toLowerCase().includes(query) ||
            user.ragione_sociale?.toLowerCase().includes(query) ||
            user.partita_iva?.includes(query)
        );
        setFilteredUsers(filtered);
    };

    const openEditDialog = (user) => {
        setEditingUser(user);
        setEditForm({
            nome: user.nome || '',
            cognome: user.cognome || '',
            ragione_sociale: user.ragione_sociale || '',
            partita_iva: user.partita_iva || '',
            telefono: user.telefono || '',
            indirizzo: user.indirizzo || '',
            citta: user.citta || '',
            cap: user.cap || '',
            provincia: user.provincia || ''
        });
        setEditDialogOpen(true);
    };

    const handleEditUser = async () => {
        setSubmitting(true);
        try {
            await updateUser(editingUser.id, editForm);
            await loadUsers();
            setEditDialogOpen(false);
            toast.success('Utente aggiornato');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
        } finally {
            setSubmitting(false);
        }
    };

    const openResetDialog = (user) => {
        setResetUser(user);
        setNewPassword('');
        setGeneratedPassword('');
        setSendEmail(true);
        setCopied(false);
        setResetDialogOpen(true);
    };

    const handleResetPassword = async () => {
        setSubmitting(true);
        try {
            const response = await resetUserPassword(resetUser.id, newPassword, sendEmail);
            
            if (!sendEmail && response.data.new_password) {
                setGeneratedPassword(response.data.new_password);
                toast.success('Password reimpostata');
            } else {
                setResetDialogOpen(false);
                toast.success('Password reimpostata e inviata via email');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nel reset della password');
        } finally {
            setSubmitting(false);
        }
    };

    const copyPassword = () => {
        navigator.clipboard.writeText(generatedPassword);
        setCopied(true);
        toast.success('Password copiata');
        setTimeout(() => setCopied(false), 2000);
    };

    const openDeleteDialog = (user) => {
        setDeleteTarget(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteUser = async () => {
        setSubmitting(true);
        try {
            await deleteUser(deleteTarget.id);
            await loadUsers();
            setDeleteDialogOpen(false);
            toast.success('Utente eliminato');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/operator')}
                                className="text-slate-600"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Dashboard
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Card className="border-slate-200">
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                                <CardTitle className="text-xl flex items-center gap-2">
                                    <Users className="w-5 h-5 text-indigo-500" />
                                    Gestione Utenti
                                </CardTitle>
                                <CardDescription className="mt-1">
                                    {users.length} utenti registrati
                                </CardDescription>
                            </div>
                            <div className="relative w-full sm:w-72">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    placeholder="Cerca per nome, email, P.IVA..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                    data-testid="search-users-input"
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Utente</TableHead>
                                        <TableHead>Azienda</TableHead>
                                        <TableHead>Contatti</TableHead>
                                        <TableHead>Registrato</TableHead>
                                        <TableHead className="text-right">Azioni</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredUsers.map((user) => (
                                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium text-slate-900">
                                                        {user.nome} {user.cognome}
                                                    </p>
                                                    <p className="text-sm text-slate-500">{user.email}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="text-sm text-slate-700">
                                                        {user.ragione_sociale || '-'}
                                                    </p>
                                                    {user.partita_iva && (
                                                        <p className="text-xs text-slate-400">
                                                            P.IVA: {user.partita_iva}
                                                        </p>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-slate-600">
                                                    {user.telefono || '-'}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-slate-500">
                                                    {formatDate(user.created_at)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openEditDialog(user)}
                                                        title="Modifica"
                                                        data-testid={`edit-user-${user.id}`}
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openResetDialog(user)}
                                                        title="Reset Password"
                                                        className="text-amber-600 hover:text-amber-700"
                                                        data-testid={`reset-pwd-${user.id}`}
                                                    >
                                                        <Key className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openDeleteDialog(user)}
                                                        title="Elimina"
                                                        className="text-red-600 hover:text-red-700"
                                                        data-testid={`delete-user-${user.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredUsers.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                                                {searchQuery ? 'Nessun utente trovato' : 'Nessun utente registrato'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            </main>

            {/* Edit User Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Modifica Utente</DialogTitle>
                        <DialogDescription>
                            {editingUser?.email}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4 py-4">
                        <div>
                            <Label htmlFor="edit-nome">Nome</Label>
                            <Input
                                id="edit-nome"
                                value={editForm.nome}
                                onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label htmlFor="edit-cognome">Cognome</Label>
                            <Input
                                id="edit-cognome"
                                value={editForm.cognome}
                                onChange={(e) => setEditForm({ ...editForm, cognome: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label htmlFor="edit-ragione">Ragione Sociale</Label>
                            <Input
                                id="edit-ragione"
                                value={editForm.ragione_sociale}
                                onChange={(e) => setEditForm({ ...editForm, ragione_sociale: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label htmlFor="edit-piva">Partita IVA</Label>
                            <Input
                                id="edit-piva"
                                value={editForm.partita_iva}
                                onChange={(e) => setEditForm({ ...editForm, partita_iva: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label htmlFor="edit-telefono">Telefono</Label>
                            <Input
                                id="edit-telefono"
                                value={editForm.telefono}
                                onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label htmlFor="edit-indirizzo">Indirizzo</Label>
                            <Input
                                id="edit-indirizzo"
                                value={editForm.indirizzo}
                                onChange={(e) => setEditForm({ ...editForm, indirizzo: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label htmlFor="edit-citta">Città</Label>
                            <Input
                                id="edit-citta"
                                value={editForm.citta}
                                onChange={(e) => setEditForm({ ...editForm, citta: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label htmlFor="edit-cap">CAP</Label>
                                <Input
                                    id="edit-cap"
                                    value={editForm.cap}
                                    onChange={(e) => setEditForm({ ...editForm, cap: e.target.value })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit-provincia">Prov.</Label>
                                <Input
                                    id="edit-provincia"
                                    value={editForm.provincia}
                                    onChange={(e) => setEditForm({ ...editForm, provincia: e.target.value })}
                                    className="mt-1"
                                    maxLength={2}
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                            Annulla
                        </Button>
                        <Button
                            onClick={handleEditUser}
                            disabled={submitting}
                            className="bg-indigo-500 hover:bg-indigo-600"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Salva
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogDescription>
                            Reimposta la password per {resetUser?.nome} {resetUser?.cognome}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        {!generatedPassword ? (
                            <>
                                <div>
                                    <Label htmlFor="new-password">Nuova Password (opzionale)</Label>
                                    <Input
                                        id="new-password"
                                        type="text"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Lascia vuoto per generare automaticamente"
                                        className="mt-1"
                                        data-testid="new-password-input"
                                    />
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="send-email"
                                        checked={sendEmail}
                                        onCheckedChange={setSendEmail}
                                    />
                                    <Label htmlFor="send-email" className="text-sm font-normal cursor-pointer">
                                        Invia la nuova password via email all'utente
                                    </Label>
                                </div>
                            </>
                        ) : (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-sm text-green-800 mb-2">
                                    Password generata con successo. Copiala e comunicala all'utente:
                                </p>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 bg-white px-3 py-2 rounded border font-mono text-sm">
                                        {generatedPassword}
                                    </code>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={copyPassword}
                                    >
                                        {copied ? (
                                            <Check className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        {!generatedPassword ? (
                            <>
                                <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
                                    Annulla
                                </Button>
                                <Button
                                    onClick={handleResetPassword}
                                    disabled={submitting}
                                    className="bg-amber-500 hover:bg-amber-600"
                                    data-testid="confirm-reset-password"
                                >
                                    {submitting ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Key className="w-4 h-4 mr-2" />
                                    )}
                                    Reimposta Password
                                </Button>
                            </>
                        ) : (
                            <Button onClick={() => setResetDialogOpen(false)}>
                                Chiudi
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete User Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare questo utente?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Stai per eliminare l'utente <strong>{deleteTarget?.nome} {deleteTarget?.cognome}</strong> ({deleteTarget?.email}). 
                            Questa azione non può essere annullata.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteUser}
                            className="bg-red-500 hover:bg-red-600"
                            disabled={submitting}
                            data-testid="confirm-delete-user"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
